import React from 'react';
import { styled, keyframes } from '@mui/material/styles';
import { Box } from '@mui/material';

// 優化的滾動動畫 - 使用 transform3d 觸發GPU加速
const scrollAnimation = keyframes`
  0% {
    transform: translate3d(100%, 0, 0);
  }
  100% {
    transform: translate3d(-100%, 0, 0);
  }
`;

// 簡化的發光效果 - 減少顏色變化
const simpleGlow = keyframes`
  0%, 100% {
    opacity: 0.8;
  }
  50% {
    opacity: 1;
  }
`;

// 用戶資料卡片容器樣式 - 移除複雜背景動畫
const ProfileCard = styled(Box)(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(3),
  borderRadius: '12px',
  overflow: 'hidden',
  width: '1000px',
  height: '80px',
  background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.8), rgba(30, 30, 30, 0.9))',
  border: '1px solid rgba(0, 212, 255, 0.3)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  
  // 靜態背景光效，避免動畫
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 212, 255, 0.05)',
    zIndex: 0,
  },
}));

// 滾動文字容器
const ScrollingContainer = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  zIndex: 2,
  pointerEvents: 'none',
});

// 優化的滾動文字樣式
const ScrollingText = styled('div')({
  display: 'inline-block',
  whiteSpace: 'nowrap',
  fontSize: '28px',
  fontWeight: 'bold',
  color: '#00d4ff',
  // 使用 transform3d 觸發GPU加速，並減少動畫複雜度
  animation: `${scrollAnimation} 15s linear infinite, ${simpleGlow} 3s ease-in-out infinite`,
  // 移除複雜的漸變和文字效果
  textShadow: '0 0 10px rgba(0, 212, 255, 0.6)',
  // 使用 will-change 提示瀏覽器優化
  willChange: 'transform',
  // 使用 GPU 層合成
  transform: 'translateZ(0)',
});

// 移除複雜的星星裝飾，使用簡單的靜態元素
const SimpleDecoration = styled('div')({
  position: 'absolute',
  fontSize: '12px',
  color: '#00d4ff',
  opacity: 0.6,
  zIndex: 1,
  pointerEvents: 'none',
  
  '&:nth-of-type(1)': {
    top: '20%',
    left: '15%',
  },
  '&:nth-of-type(2)': {
    top: '30%',
    right: '20%',
  },
  '&:nth-of-type(3)': {
    bottom: '25%',
    left: '25%',
  },
});

// 優化的ScrollingWelcomeText組件
const OptimizedScrollingWelcomeText = ({ disabled = false }) => {
  // 添加禁用動畫的選項
  const TextComponent = disabled ? 
    styled('div')({
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#00d4ff',
      textAlign: 'center',
      padding: '20px',
    }) : 
    ScrollingText;

  if (disabled) {
    return (
      <ProfileCard>
        <TextComponent>
          歡迎回來，在這里你永遠不會因為言論而被封號
        </TextComponent>
      </ProfileCard>
    );
  }

  return (
    <ProfileCard>
      {/* 簡化的靜態裝飾 */}
      <SimpleDecoration>⭐</SimpleDecoration>
      <SimpleDecoration>✨</SimpleDecoration>
      <SimpleDecoration>🌟</SimpleDecoration>
      
      {/* 滾動文字層 */}
      <ScrollingContainer>
        <ScrollingText>
          歡迎回來，在這里你永遠不會因為言論而被封號
        </ScrollingText>
      </ScrollingContainer>
    </ProfileCard>
  );
};

// 更進一步的性能優化版本 - 可選擇完全靜態
const StaticWelcomeText = () => {
  const StaticCard = styled(Box)(({ theme }) => ({
    position: 'relative',
    padding: theme.spacing(3),
    borderRadius: '12px',
    width: '1000px',
    height: '80px',
    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.8), rgba(30, 30, 30, 0.9))',
    border: '1px solid rgba(0, 212, 255, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }));

  const StaticText = styled('div')({
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#00d4ff',
    textShadow: '0 0 10px rgba(0, 212, 255, 0.6)',
  });

  return (
    <StaticCard>
      <StaticText>
        歡迎回來，在這里你永遠不會因為言論而被封號
      </StaticText>
    </StaticCard>
  );
};

// 主導出組件 - 可以根據需要切換
const ScrollingWelcomeText = ({ performance = 'optimized' }) => {
  switch (performance) {
    case 'static':
      return <StaticWelcomeText />;
    case 'disabled':
      return <OptimizedScrollingWelcomeText disabled={true} />;
    case 'optimized':
    default:
      return <OptimizedScrollingWelcomeText />;
  }
};

export default ScrollingWelcomeText;